import {
  Box,
  Container,
  Typography,
  SpeedDial,
  SpeedDialIcon,
  SpeedDialAction,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Link,
  Button,
  DialogContentText,
  ThemeProvider,
} from '@mui/material';
import {
  ChevronLeft,
  DesktopWindowsOutlined,
  ErrorOutline,
} from '@mui/icons-material';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from 'components/navbar';
import { screenBreakpoints } from 'configs/app-config.js';
import ReactGA from 'react-ga';
import GameContainer from 'components/gameContainer';
import {
  GamePlatformBadge,
  isGameSupportedOnPlatform,
  useGamePlatform,
} from 'features/v2/GamePlatformSupport';
import { productionGames } from 'features/v2/presentation';
import v2Theme, { v2Tokens } from 'theme/v2Theme';
import 'styles/v2.css';

const GamePage = () => {
  const smallScreen = window.innerWidth < screenBreakpoints.md;
  ReactGA.initialize('G-RF6770D6JZ');
  const { gameName } = useParams();
  const navigate = useNavigate();
  const activeGame = productionGames.find((game) => game.id === gameName);
  const gameTitle = activeGame ? activeGame.title : gameName;
  const currentPlatform = useGamePlatform();
  const isPlatformBlocked =
    activeGame && !isGameSupportedOnPlatform(activeGame, currentPlatform);

  // useStates
  const isMobile = false;
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  const handleReportDialogOpen = () => {
    setReportDialogOpen(true);
  };

  const handleReportDialogClose = () => {
    setReportDialogOpen(false);
  };

  // Google analytics Page View
  useEffect(() => {
    ReactGA.pageview(window.location.pathname + window.location.search);
  }, []);

  const dialActions = [
    {
      icon: <ErrorOutline />,
      name: 'Report Problems',
      action: handleReportDialogOpen,
    },
  ];

  // Just in case we need the mobile version even a simple one
  // const isNonMobileScreens = useMediaQuery('(min-width:1000px)');
  return (
    <ThemeProvider theme={v2Theme}>
      <Box className="v2-shell v2-game-shell">
        <Navbar />
        <Container
          component="main"
          maxWidth={false}
          sx={{ maxWidth: 1280, px: { xs: 2, sm: 3 }, py: { xs: 3, md: 6 } }}
        >
          <Box sx={{ mb: { xs: 2.5, md: 3 } }}>
            <Button
              variant="text"
              color="inherit"
              startIcon={<ChevronLeft />}
              onClick={() => navigate('/')}
              sx={{
                minHeight: 32,
                px: 0,
                color: 'text.secondary',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                fontSize: 11,
                letterSpacing: 0,
                textTransform: 'none',
                '&:hover': { color: '#fff', backgroundColor: 'transparent' },
              }}
            >
              Back to Game Lobby
            </Button>
            <Typography
              component="h1"
              sx={{
                mt: 0.5,
                color: '#fff',
                fontSize: { xs: 26, sm: 32 },
                fontWeight: 900,
                lineHeight: 1.1,
                letterSpacing: '-0.035em',
              }}
            >
              {isPlatformBlocked ? gameTitle : `Playing: ${gameTitle}`}
            </Typography>
          </Box>

          {isPlatformBlocked ? (
            <Box
              role="status"
              sx={{
                display: 'grid',
                justifyItems: 'center',
                minHeight: 320,
                px: 3,
                py: 6,
                border: '1px solid rgba(94,202,255,0.22)',
                borderRadius: v2Tokens.radius.panel,
                background:
                  'radial-gradient(circle at 50% 0%, rgba(62,166,255,0.10), transparent 55%), #07090D',
                boxShadow: '0 24px 60px rgba(0,0,0,0.28)',
                textAlign: 'center',
              }}
            >
              <DesktopWindowsOutlined
                aria-hidden="true"
                sx={{ mb: 2, color: '#5ECAFF', fontSize: 48 }}
              />
              <GamePlatformBadge game={activeGame} />
              <Typography variant="h2" sx={{ mt: 2, color: '#fff' }}>
                {gameTitle} requires a desktop browser
              </Typography>
              <Typography
                color="text.secondary"
                sx={{ maxWidth: 480, mt: 1.5, fontSize: 13, lineHeight: 1.7 }}
              >
                This game is available for discovery here, but it cannot be
                launched on a mobile device. Return on desktop to play.
              </Typography>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => navigate('/')}
                sx={{ mt: 3 }}
              >
                Back to Game Lobby
              </Button>
            </Box>
          ) : (
            <Box
              className="v2-game-frame"
              sx={{
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: v2Tokens.radius.panel,
                backgroundColor: '#07090D',
                boxShadow: '0 24px 60px rgba(0,0,0,0.28)',
                px: { xs: 1, sm: 2, md: 3 },
                pt: { xs: 1, sm: 2 },
                pb: { xs: 2, md: 3 },
              }}
            >
              <GameContainer />
            </Box>
          )}
        </Container>

        {!isMobile && (
          <SpeedDial
            ariaLabel="Report a problem"
            sx={{
              position: 'fixed',
              bottom: smallScreen ? 20 : 56,
              right: smallScreen ? 10 : 40,
            }}
            icon={<SpeedDialIcon />}
          >
            {dialActions.map((action) => (
              <SpeedDialAction
                key={action.name}
                icon={action.icon}
                tooltipTitle={action.name}
                onClick={action.action}
              />
            ))}
          </SpeedDial>
        )}

        <Dialog open={reportDialogOpen} onClose={handleReportDialogClose}>
          <DialogTitle>Any Issues or Bugs?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Please visit our{' '}
              <Link href="https://twitter.com/OrdinalsPlay">Twitter</Link> and DM
              us. We will listen to your feedbacks. Thank you so much.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleReportDialogClose}>Cancel</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default GamePage;
